import { Divider, Stack, Typography } from '@mui/material';
import { JobStatus, type BackupJob, type RestoreJob } from '@repo/api-gw-sdk';

import { StatusCardWrapper } from '@/components/panels/instance/tabs/statusCardWrapper';
import { isRestoreJob } from '@/components/queryBuilder/properties';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { JobStatuses } from '@/data/inventory/data';
import { formatDuration, isDateValid } from '@/utils/dateTime';
import dayjs, { DATE_FORMAT, TIME_FORMAT } from '@/utils/dayjs';

import { JobStatusIcon } from './jobStatusIcon';

export const JobStatusCard = ({ job }: { job: BackupJob | RestoreJob }) => {
  return (
    <Stack paddingX='40px' margin={'40px 0'}>
      <StatusCardWrapper
        borderColor={JobStatuses[job.jobExecutionDetails.status].iconColor}
      >
        <Stack gap='32px'>
          <JobStatusHeader job={job} />
          <Divider sx={{ margin: '0 40px', borderStyle: 'dashed' }} />
          <JobStatusFooter job={job} />
        </Stack>
      </StatusCardWrapper>
    </Stack>
  );
};

const JobStatusFooter = ({ job }: { job: BackupJob | RestoreJob }) => {
  let startText = 'Start time';
  let endText = 'End time';
  let start: Date | undefined = job.jobExecutionDetails.startTime;
  let end: Date | undefined = job.jobExecutionDetails.endTime;

  if (job.jobExecutionDetails.status === JobStatus.JOB_PENDING) {
    startText = 'Creation time';
    start = job.jobExecutionDetails.creationTime;
    endText = 'Expected start time';
    end = job.jobExecutionDetails.expectedStartTime;
  }

  const accessDeniedItemsCount = 0; // todo: get from server
  return (
    <Stack gap='24px'>
      <JobTimeRangeStatus
        startText={startText}
        endText={endText}
        start={start}
        end={end}
        shouldHaveEnd={job.jobExecutionDetails.status !== JobStatus.JOB_RUNNING}
      />
      {job.jobExecutionDetails.vaultId && (
        <SnapshotToolbar
          pointInTime={job.jobExecutionDetails.startTime}
          vaultId={job.jobExecutionDetails.vaultId}
          accessDeniedItemsCount={accessDeniedItemsCount}
          hideAccessDeniedItemsCount={true}
        />
      )}
    </Stack>
  );
};

const JobTimeRangeStatus = ({
  startText,
  endText,
  start,
  end,
  shouldHaveEnd = true,
}: {
  startText: string;
  endText: string;
  start?: Date;
  end?: Date;
  shouldHaveEnd?: boolean;
}) => {
  return (
    <Stack gap='12px' paddingX='40px'>
      {isDateValid(start) && (
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Typography>{startText}</Typography>
          <JobDateStatus date={start} />
        </Stack>
      )}
      {!shouldHaveEnd && <Typography>--</Typography>}
      {shouldHaveEnd && isDateValid(end) && (
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Typography>{endText}</Typography>
          <JobDateStatus date={end} />
        </Stack>
      )}
    </Stack>
  );
};

const JobDateStatus = ({ date }: { date?: Date }) => {
  return (
    <Stack direction='row' alignItems='center' justifyContent='space-between'>
      <Stack direction='row' alignItems='center' gap='12px'>
        <Typography>{dayjs.utc(date).format(DATE_FORMAT)}</Typography>
        <Typography>{dayjs.utc(date).format(TIME_FORMAT)} UTC</Typography>
      </Stack>
    </Stack>
  );
};

const JobDuration = ({ job }: { job: BackupJob | RestoreJob }) => {
  const status = job.jobExecutionDetails.status;
  if (job.jobExecutionDetails.status === JobStatus.JOB_PENDING) {
    return (
      isDateValid(job.jobExecutionDetails.expectedStartTime) && (
        <Typography variant='body2'>
          Starts in{' '}
          {formatDuration(
            dayjs(job.jobExecutionDetails.expectedStartTime)
              .utc()
              .diff(dayjs().utc(), 'seconds')
          )}
        </Typography>
      )
    );
  }

  if (job.jobExecutionDetails.status === JobStatus.JOB_RUNNING) {
    return (
      <Typography variant='body2'>
        Running for{' '}
        {formatDuration(
          dayjs()
            .utc()
            .diff(dayjs(job.jobExecutionDetails.startTime), 'seconds')
        )}
      </Typography>
    );
  }

  if (job.jobExecutionDetails.durationSeconds) {
    const label = status === JobStatus.JOB_FAILED ? 'Failed in' : 'Finished in';
    return (
      <Typography variant='body2'>
        {label} {formatDuration(job.jobExecutionDetails.durationSeconds)}
      </Typography>
    );
  }

  return null;
};

const JobStatusHeader = ({ job }: { job: BackupJob | RestoreJob }) => {
  return (
    <Stack
      alignItems='center'
      justifyContent='space-between'
      width='100%'
      gap='12px'
      paddingX='40px'
    >
      <Stack
        width='100%'
        direction='row'
        alignItems='center'
        justifyContent='space-between'
      >
        <Typography variant='h4'>Job status</Typography>
        <JobDuration job={job} />
      </Stack>
      <JobStatusIcon jobStatus={job.jobExecutionDetails.status} />
      <Typography variant='h3'>
        {isRestoreJob(job) ? 'Restore' : 'Backup'}{' '}
        {JobStatuses[job.jobExecutionDetails.status].title.toLowerCase()}
      </Typography>
      <Typography variant='body1'>
        {job.jobExecutionDetails.errorMessage}
      </Typography>
    </Stack>
  );
};
